import { useCallback, useEffect, useMemo, useState } from "react";

export const REQUEST_LOG_GROUPS = ["Page", "Asset", "API", "Other"] as const;
export type RequestLogGroup = (typeof REQUEST_LOG_GROUPS)[number];

export type RequestLogGroupFilters = Record<RequestLogGroup | "bots" | "humans", boolean>;

export const REQUEST_LOG_SOURCES = ["nginx", "node", "origin", "browser_cache", "cloudfront"] as const;
export type RequestLogSourceFilter = (typeof REQUEST_LOG_SOURCES)[number];

export const REQUEST_LOG_STATUS_BANDS = ["2xx", "3xx", "4xx", "5xx"] as const;
export type RequestLogStatusBand = (typeof REQUEST_LOG_STATUS_BANDS)[number];

export const REQUEST_LOG_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "other"] as const;
export type RequestLogMethodFilter = (typeof REQUEST_LOG_METHODS)[number];

export type RequestLogStagingFilter = "all" | "production" | "staging";

export type RequestLogFiltersState = {
  search: string;
  groups: RequestLogGroupFilters;
  sources: Record<RequestLogSourceFilter, boolean>;
  statusBands: Record<RequestLogStatusBand, boolean>;
  methods: Record<RequestLogMethodFilter, boolean>;
  staging: RequestLogStagingFilter;
};

export type RequestLogPreferences = {
  paused: boolean;
  filters: RequestLogFiltersState;
};

export const DEFAULT_GROUP_FILTERS: RequestLogGroupFilters = {
  Page: true,
  Asset: true,
  API: true,
  Other: true,
  bots: true,
  humans: true
};

export const DEFAULT_SOURCE_FILTERS: Record<RequestLogSourceFilter, boolean> = {
  nginx: true,
  node: true,
  origin: true,
  browser_cache: true,
  cloudfront: true
};

export const DEFAULT_STATUS_FILTERS: Record<RequestLogStatusBand, boolean> = {
  "2xx": true,
  "3xx": true,
  "4xx": true,
  "5xx": true
};

export const DEFAULT_METHOD_FILTERS: Record<RequestLogMethodFilter, boolean> = {
  GET: true,
  POST: true,
  PUT: true,
  PATCH: true,
  DELETE: true,
  HEAD: true,
  other: true
};

export const DEFAULT_REQUEST_LOG_FILTERS: RequestLogFiltersState = {
  search: "",
  groups: DEFAULT_GROUP_FILTERS,
  sources: DEFAULT_SOURCE_FILTERS,
  statusBands: DEFAULT_STATUS_FILTERS,
  methods: DEFAULT_METHOD_FILTERS,
  staging: "all"
};

const DEFAULT_PREFS: RequestLogPreferences = {
  paused: false,
  filters: DEFAULT_REQUEST_LOG_FILTERS
};

function storageKey(site: string, userKey: string): string {
  const s = String(site || "").trim().toLowerCase() || "_";
  const u = String(userKey || "").trim().toLowerCase() || "anonymous";
  return `sitewrap.requestLog.prefs.v1:${s}:${u}`;
}

function mergeFilters(raw: unknown): RequestLogFiltersState {
  const base = DEFAULT_REQUEST_LOG_FILTERS;
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;
  const groups = { ...base.groups };
  if (o.groups && typeof o.groups === "object") {
    for (const k of Object.keys(groups) as (keyof RequestLogGroupFilters)[]) {
      const v = (o.groups as Record<string, unknown>)[k];
      if (typeof v === "boolean") groups[k] = v;
    }
  }
  const sources = { ...base.sources };
  if (o.sources && typeof o.sources === "object") {
    for (const k of REQUEST_LOG_SOURCES) {
      const v = (o.sources as Record<string, unknown>)[k];
      if (typeof v === "boolean") sources[k] = v;
    }
  }
  const statusBands = { ...base.statusBands };
  if (o.statusBands && typeof o.statusBands === "object") {
    for (const k of REQUEST_LOG_STATUS_BANDS) {
      const v = (o.statusBands as Record<string, unknown>)[k];
      if (typeof v === "boolean") statusBands[k] = v;
    }
  }
  const methods = { ...base.methods };
  if (o.methods && typeof o.methods === "object") {
    for (const k of REQUEST_LOG_METHODS) {
      const v = (o.methods as Record<string, unknown>)[k];
      if (typeof v === "boolean") methods[k] = v;
    }
  }
  const staging =
    o.staging === "staging" || o.staging === "production" || o.staging === "all"
      ? o.staging
      : base.staging;
  return {
    search: typeof o.search === "string" ? o.search : base.search,
    groups,
    sources,
    statusBands,
    methods,
    staging
  };
}

function readPrefs(site: string, userKey: string): RequestLogPreferences {
  try {
    const raw = localStorage.getItem(storageKey(site, userKey));
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      paused: Boolean(parsed.paused),
      filters: mergeFilters(parsed.filters)
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePrefs(site: string, userKey: string, prefs: RequestLogPreferences): void {
  try {
    localStorage.setItem(storageKey(site, userKey), JSON.stringify(prefs));
  } catch { /* quota / private mode */ }
}

export function requestLogRowKey(e: { id?: unknown; ts?: unknown; url?: unknown }, index: number): string {
  const id = e.id != null && String(e.id).trim() !== "" ? String(e.id) : "";
  const ts = e.ts != null ? String(e.ts) : "";
  const url = String(e.url || "");
  if (id && ts) return `${id}:${ts}`;
  if (id) return `${id}:${url}:${index}`;
  return `row:${index}:${ts}:${url}`;
}

export function useRequestLogPreferences(site: string, userKey: string) {
  const key = useMemo(() => storageKey(site, userKey), [site, userKey]);
  const [prefs, setPrefs] = useState<RequestLogPreferences>(() => readPrefs(site, userKey));

  useEffect(() => {
    setPrefs(readPrefs(site, userKey));
  }, [key, site, userKey]);

  const persist = useCallback(
    (next: RequestLogPreferences) => {
      setPrefs(next);
      writePrefs(site, userKey, next);
    },
    [site, userKey]
  );

  const setPaused = useCallback(
    (paused: boolean) => {
      persist({ ...prefs, paused });
    },
    [persist, prefs]
  );

  const setFilters = useCallback(
    (updater: RequestLogFiltersState | ((prev: RequestLogFiltersState) => RequestLogFiltersState)) => {
      const nextFilters = typeof updater === "function" ? updater(prefs.filters) : updater;
      persist({ ...prefs, filters: nextFilters });
    },
    [persist, prefs]
  );

  const resetFilters = useCallback(() => {
    persist({ ...prefs, filters: DEFAULT_REQUEST_LOG_FILTERS });
  }, [persist, prefs]);

  return {
    paused: prefs.paused,
    filters: prefs.filters,
    setPaused,
    setFilters,
    resetFilters
  };
}
